import { type CSSProperties, type RefObject, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { openTotal, useAlertSummary } from "../../lib/alerts";
import { avatarHue, initialsOf } from "../../lib/avatar";
import { DESTINATIONS, type Destination } from "../../lib/destinations";
import {
  browserStore,
  readCollapsedGroups,
  toggleGroup,
  writeCollapsedGroups,
} from "../../lib/railPreference";
import { useSession } from "../../lib/session";
import { isMacPlatform, pagesFor } from "../../lib/spotlight";
import { Button } from "../ui/Button";
import { MicroLabel } from "../ui/Text";

/**
 * The floating left rail.
 *
 * Nav items are a plain vertical list with a signal-coloured left edge on the
 * active one — no pill, no fill, no icon. Icons here would be decoration: the
 * destinations have unambiguous names, and a 16px glyph for "Allowance policies"
 * communicates nothing the word does not.
 *
 * The one exception is the alert count, which is not decoration: it is the only
 * thing in the rail that changes on its own, and it is what makes "Alerts" worth
 * having in the list at all when nothing is wrong.
 *
 * The rail is `<nav>` inside the shell's `<header>`, and the active item carries
 * `aria-current` via `NavLink`, so the structure is navigable without sight.
 */

interface NavRailProps {
  /** Slid out of view on `lg` and up; ignored below, where the strip is static. */
  readonly isHidden: boolean;
  readonly onHide: () => void;
  /** So the shell can return focus here when the rail comes back. */
  readonly hideButtonRef: RefObject<HTMLButtonElement | null>;
  /** Opens the spotlight; the shortcut does the same from anywhere. */
  readonly onSearch: () => void;
}

export function NavRail({ isHidden, onHide, hideButtonRef, onSearch }: NavRailProps) {
  const { principal, can, signOut } = useSession();
  const alerts = useAlertSummary();
  const open = openTotal(alerts.data);
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => readCollapsedGroups(browserStore()));

  const toggle = (group: string) => {
    const next = toggleGroup(collapsed, group);
    setCollapsed(next);
    writeCollapsedGroups(browserStore(), next);
  };

  return (
    <header
      id="nav-rail"
      className={[
        "z-20 m-(--rail-inset) flex flex-col overflow-hidden rounded-panel border border-rule bg-paper-raised shadow-float",
        "lg:fixed lg:inset-y-0 lg:left-0 lg:w-(--rail-width)",
        "duration-stage ease-swiss",
        // Hiding, visibility rides the slide so the rail leaves the tab order and
        // the accessibility tree once it has gone, not before. Showing, it must
        // flip at once: a transitioned visibility is still `hidden` on the first
        // frame, and the shell moves focus into the rail on that frame.
        isHidden
          ? "transition-[translate,visibility] lg:invisible lg:-translate-x-[calc(100%+var(--rail-inset)*2)]"
          : "transition-[translate]",
      ].join(" ")}
    >
      <div className="relative flex items-baseline justify-between gap-4 px-gutter pt-5 pb-4 lg:flex-col lg:items-start lg:justify-start lg:gap-1 lg:pt-6 lg:pb-5">
        {/* The wordmark sets the type direction for everything else: display
            face, tight tracking, and the one place the signal colour appears
            without signalling anything. */}
        <span className="font-display text-title font-semibold text-ink">
          OCPP<span className="text-signal">.</span>
        </span>
        <MicroLabel>Central system</MicroLabel>
        <button
          ref={hideButtonRef}
          type="button"
          onClick={onHide}
          aria-label="Hide navigation"
          aria-controls="nav-rail"
          aria-expanded={true}
          className={[
            "absolute top-5 right-3 hidden size-7 items-center justify-center lg:inline-flex",
            "rounded-hairline border border-transparent text-ink-faint",
            "transition-colors duration-fast ease-swiss hover:border-rule hover:text-ink",
          ].join(" ")}
        >
          <RailToggleGlyph direction="hide" />
        </button>
      </div>

      {/* Shaped like a field because that is what it opens, with the shortcut
          printed on it: the button is how somebody learns the shortcut exists. */}
      <div className="px-gutter pb-3 lg:pb-4">
        <button
          type="button"
          onClick={onSearch}
          aria-keyshortcuts={isMacPlatform() ? "Meta+K" : "Control+K"}
          className={[
            "flex w-full items-center gap-2 rounded-item border border-rule bg-paper px-2.5 py-1.5 text-left",
            "text-body text-ink-faint transition-colors duration-fast ease-swiss hover:border-ink-faint hover:text-ink-muted",
          ].join(" ")}
        >
          <svg
            viewBox="0 0 16 16"
            width="14"
            height="14"
            fill="none"
            aria-hidden="true"
            className="shrink-0"
          >
            <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.25" />
            <path
              d="m10.5 10.5 3.25 3.25"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinecap="round"
            />
          </svg>
          <span className="flex-1">Search</span>
          <kbd
            className="rounded-hairline border border-rule px-1 text-micro tracking-normal"
            aria-hidden="true"
          >
            {shortcutLabel()}
          </kbd>
        </button>
      </div>

      <nav
        aria-label="Sections"
        className="scroll-slim flex gap-6 overflow-x-auto px-gutter pb-3 lg:flex-1 lg:flex-col lg:gap-4 lg:overflow-x-visible lg:overflow-y-auto lg:px-0 lg:pb-4"
      >
        {groupsOf(pagesFor(DESTINATIONS, can)).map(([group, items]) => {
          const isCollapsed = collapsed.includes(group);
          const listId = `nav-group-${group.toLowerCase()}`;
          const holdsAlerts = items.some((item) => item.badge === "alerts") && open > 0;
          const holdsCurrent = items.some((item) => isWithin(pathname, item.to));

          return (
            <div key={group} className="flex shrink-0 flex-col">
              <button
                type="button"
                onClick={() => toggle(group)}
                aria-expanded={!isCollapsed}
                aria-controls={listId}
                className={[
                  "group/caption flex items-center gap-2 pb-1.5 text-left lg:px-gutter",
                  "text-ink-faint transition-colors duration-fast ease-swiss hover:text-ink",
                ].join(" ")}
              >
                <span className="text-micro font-medium uppercase">{group}</span>
                {/* What a folded group would otherwise hide: that the page on
                    screen lives in it, and an alert count. Both only while
                    folded — open, the item itself says it. */}
                {isCollapsed && holdsCurrent ? (
                  <>
                    <span className="size-1.5 rounded-full bg-signal" aria-hidden="true" />
                    <span className="sr-only">, contains the current page</span>
                  </>
                ) : null}
                {isCollapsed && holdsAlerts ? (
                  <span className="rounded-hairline bg-signal px-1.5 py-0.5 text-micro font-semibold text-paper">
                    {open}
                    <span className="sr-only"> open alerts</span>
                  </span>
                ) : null}
                <svg
                  viewBox="0 0 12 12"
                  width="10"
                  height="10"
                  aria-hidden="true"
                  className={[
                    "ml-auto opacity-0 transition-[rotate,opacity] duration-fast ease-swiss",
                    "group-hover/caption:opacity-100 group-focus-visible/caption:opacity-100",
                    isCollapsed ? "-rotate-90 opacity-100" : "",
                  ].join(" ")}
                >
                  <path
                    d="M3 4.5 6 7.5l3-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {/* The 0fr→1fr row is what lets a list of unknown height animate
                  shut (the child needs `min-h-0`, or its content height wins).
                  Visibility is what takes the folded links out of the tab order
                  and the accessibility tree, which a zero-height box alone does
                  not; as with the rail, it rides the transition only when
                  folding, so opening is never a frame late. Below `lg` the strip
                  is horizontal, where a zero-height row still holds its width,
                  so there a folded list is simply removed. */}
              <div
                id={listId}
                className={[
                  "grid duration-stage ease-swiss",
                  isCollapsed
                    ? "invisible grid-rows-[0fr] transition-[grid-template-rows,visibility] max-lg:hidden"
                    : "grid-rows-[1fr] transition-[grid-template-rows]",
                ].join(" ")}
              >
                {/* Inset under the caption on a hairline guide, so the items read
                    as the group's children; the active item's mark sits on that
                    guide rather than at the card's edge. */}
                <div className="flex min-h-0 gap-4 overflow-hidden lg:ml-gutter lg:flex-col lg:gap-0.5 lg:border-l lg:border-rule">
                  {items.map((destination) => (
                    <NavLink
                      key={destination.to}
                      to={destination.to}
                      className={({ isActive }) =>
                        [
                          "whitespace-nowrap border-b-2 py-1.5 text-body no-underline",
                          "lg:-ml-px lg:border-b-0 lg:border-l-2 lg:pr-gutter lg:pl-3",
                          "transition-colors duration-fast ease-swiss",
                          isActive
                            ? "border-signal font-semibold text-ink"
                            : "border-transparent text-ink-muted hover:border-ink-faint hover:text-ink",
                        ].join(" ")
                      }
                    >
                      {destination.label}
                      {destination.badge === "alerts" && open > 0 ? (
                        <span
                          className="ml-2 inline-block rounded-hairline bg-signal px-1.5 py-0.5 text-micro font-semibold text-paper"
                          /* Announced, not merely coloured: the count is the whole
                             content of the badge, and a screen reader hearing "Alerts
                             3" without it would hear a different fact. */
                          aria-label={`${open} open`}
                        >
                          {open}
                        </span>
                      ) : null}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-rule px-gutter py-3 lg:flex-col lg:items-stretch lg:gap-3 lg:py-4">
        <MicroLabel>Signed in</MicroLabel>
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={principal?.name ?? ""} />
          <div className="flex min-w-0 flex-col">
            {/* The way to one's own account, including the password form. A link
                only for a person: a service token has no account page worth
                opening. */}
            {principal?.type === "user" ? (
              <NavLink
                to="/account"
                className="truncate text-data font-medium text-ink underline decoration-rule decoration-1 underline-offset-4 hover:decoration-ink"
              >
                {principal.name}
              </NavLink>
            ) : (
              <span className="truncate text-data font-medium text-ink">
                {principal?.name ?? "—"}
              </span>
            )}
            <span className="truncate text-micro tracking-normal normal-case text-ink-faint">
              {/* Role names for a person; a token has scopes in config, not roles. */}
              {principal === undefined ? "" : principalRoleLine(principal)}
            </span>
          </div>
        </div>
        <Button variant="ghost" className="lg:self-start" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </header>
  );
}

/** Whether `pathname` is `to` or a page beneath it — `NavLink`'s own rule. */
function isWithin(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Preserves declaration order rather than sorting: the rail's order is a
 * judgement about what an operator opens most often, and alphabetising it would
 * discard that.
 */
function groupsOf(
  destinations: readonly Destination[],
): readonly (readonly [string, readonly Destination[]])[] {
  const groups = new Map<string, Destination[]>();

  for (const destination of destinations) {
    const existing = groups.get(destination.group);
    if (existing === undefined) {
      groups.set(destination.group, [destination]);
      continue;
    }
    existing.push(destination);
  }

  return [...groups.entries()];
}

/** What the rail prints under the signed-in name. */
function principalRoleLine(principal: {
  readonly type: "user" | "service";
  readonly roles: readonly { readonly name: string }[];
}): string {
  if (principal.type === "service") return "Service token";
  if (principal.roles.length === 0) return "Direct permissions only";
  return principal.roles.map((role) => role.name).join(" · ");
}

/**
 * The rail's one icon, and it earns its place where the destinations' would not:
 * a hide/show control has no noun to print, and "Hide navigation" spelled out in
 * the rail's header would outshout the wordmark. The label lives in `aria-label`.
 */
export function RailToggleGlyph({ direction }: { readonly direction: "hide" | "show" }) {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <rect
        x="1.75"
        y="2.75"
        width="12.5"
        height="10.5"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <path d="M6 2.75v10.5" stroke="currentColor" strokeWidth="1.25" />
      <path
        d={direction === "hide" ? "M10.5 6 8.5 8l2 2" : "M9 6l2 2-2 2"}
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A profile placeholder: initials on a colour of the name's own. Decorative —
 * the name is printed beside it — so it is hidden from assistive tech rather
 * than read out as "S V".
 */
function Avatar({ name }: { readonly name: string }) {
  const style = { "--avatar-hue": avatarHue(name) } as CSSProperties;

  return (
    <span
      aria-hidden="true"
      style={style}
      className="avatar inline-flex size-9 shrink-0 items-center justify-center rounded-full text-data font-semibold tracking-normal select-none"
    >
      {initialsOf(name)}
    </span>
  );
}

/** "⌘K" where the platform says it is a Mac, "Ctrl K" everywhere else. */
function shortcutLabel(): string {
  return isMacPlatform() ? "⌘K" : "Ctrl K";
}
